import type { UserEntry } from '@common/types/users';
import {
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    type SelectChangeEvent,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { updateUserSignature } from '@/services/UserService';
import { errorSnackbar, successSnackbar, warningSnackbar } from '@/utils/snackbar';

interface Props {
    users: UserEntry[];
    selectedUserId: number;
    signatureLoading: boolean;
    signature: string;
    onSelectUser(id: number | null): void;
}

export default function Signature({
    users,
    selectedUserId,
    signatureLoading,
    signature,
    onSelectUser,
}: Props) {
    const queryClient = useQueryClient();

    const [draftSignature, setDraftSignature] = useState(signature);

    useEffect(() => {
        setDraftSignature(signature);
    }, [signature]);

    const isDirty = draftSignature !== signature;

    const handleUserChange = (event: SelectChangeEvent<string>) => {
        const id = Number(event.target.value);
        if (!Number.isNaN(id)) {
            onSelectUser(id);
        }
    };

    const saveMutation = useMutation({
        mutationFn: (draft: string) => updateUserSignature(selectedUserId, draft),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['user', selectedUserId] });
            void queryClient.invalidateQueries({ queryKey: ['users'] });
            successSnackbar('Signature updated successfully');
        },
        onError: () => {
            errorSnackbar('Failed to update signature');
        },
    });

    const handleSave = () => {
        if (!selectedUserId) {
            warningSnackbar('Please select a user first');
            return;
        }
        saveMutation.mutate(draftSignature);
    };

    const disableSave = !selectedUserId || !isDirty || saveMutation.isPending || signatureLoading;

    return (
        <Stack spacing={2}>
            <Typography variant="h1">Signature Configuration</Typography>
            <FormControl sx={{ width: '100%', maxWidth: '600px' }}>
                <InputLabel>Select Name</InputLabel>
                <Select
                    label="Select User"
                    value={String(selectedUserId)}
                    onChange={handleUserChange}
                >
                    {users.map((u) => (
                        <MenuItem key={u.userId} value={u.userId}>
                            {u.fullName}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <TextField
                label="Signature of email"
                value={draftSignature}
                multiline
                minRows={4}
                onChange={(e) => setDraftSignature(e.target.value)}
                fullWidth
                placeholder={signatureLoading ? 'Loading signature…' : ''}
                disabled={!selectedUserId || signatureLoading}
                sx={{
                    width: '100%',
                    maxWidth: '600px',
                }}
            />
            <Button
                variant="contained"
                onClick={handleSave}
                disabled={disableSave}
                sx={{ width: 'fit-content' }}
            >
                {saveMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
        </Stack>
    );
}
